/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx, css, keyframes } from '@emotion/core';
import PropTypes from 'prop-types';
import { forwardRef, memo, useEffect, useMemo, useRef } from 'react';
import theme from "../../theme/settings";
import { Typography } from "../";
const cutPX = x => {
  return isNaN(x) ? x : x + 'px';
};
const validatePercent = p => {
  let per = +p.toString().replace('%', '');
  return Math.max(Math.min(per, 100), 0);
};
const Indeterminate = /*#__PURE__*/forwardRef(({
  background,
  className,
  color,
  height,
  width,
  ...props
}, ref) => {
  if (!ref) ref = useRef(null);
  const firstBarRef = useRef(null);
  const secondBarRef = useRef(null);
  const unique = {
    container: 'DGN-UI-LinearProgress-Determinate',
    firstBar: 'DGN-UI-LinearProgress-Determinate-BarFirst',
    secondBar: 'DGN-UI-LinearProgress-Determinate-BarSecond'
  };
  const animation = {
    keyframeStart: keyframes`
            from {
                max-height: 0;
                opacity: 0;
            }
            to {
                max-height: 20px;
                opacity: 1;
            }
        `,
    keyframeMove: keyframes`
            0% {
                left: -100%;
            }
            50% {
                left: 100%;
            }
            100% {
                left: 100%;
            }
        `
  };
  const containerCSS = css`
        animation: ${animation.keyframeStart} 0.3s ease-in;
        background-color: ${background};
        border-radius: 4px;
        box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.1);
        height: ${cutPX(height)};
        overflow: hidden;
        position: relative;
        width: ${cutPX(width)};
        .${unique.firstBar} {
            animation: ${animation.keyframeMove} 3s infinite;
            background-color: ${theme.colors[color] || color};
            border-radius: 4px;
            bottom: 0;
            left: 0;
            position: absolute;
            top: 0;
            transition: transform 0.3s ease-in-out;
            width: 100%;
        }
        .${unique.secondBar} {
            animation-delay: 1.5s;
            animation: ${animation.keyframeMove} 3s infinite;
            background-color: ${theme.colors[color] || color};
            border-radius: 4px;
            bottom: 0;
            left: -100%;
            position: absolute;
            top: 0;
            transition: transform 0.3s ease-in-out;
            width: 100%;
        }
    `;
  const FirstBar = useMemo(() => jsx("div", {
    className: unique.firstBar,
    ref: firstBarRef
  }), [color]);
  const SecondBar = useMemo(() => jsx("div", {
    className: unique.secondBar,
    ref: secondBarRef
  }), [color]);
  return jsx("div", {
    ...props,
    className: [unique.container, className].join(' ').trimEnd(),
    css: containerCSS,
    ref: ref
  }, FirstBar, SecondBar);
});
Indeterminate.defaultProps = {
  background: theme.colors.brand12,
  color: theme.colors.semantic.info,
  height: 8,
  width: '100%'
};
Indeterminate.propTypes = {
  background: PropTypes.string,
  className: PropTypes.string,
  color: PropTypes.string,
  height: PropTypes.number,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};
export const IndeterminateLinearProgress = /*#__PURE__*/memo(Indeterminate);
const Determinate = /*#__PURE__*/forwardRef(({
  background,
  color,
  className,
  duration,
  height,
  percent,
  percentColor,
  showValue,
  valuePosition,
  width,
  ...props
}, ref) => {
  if (!ref) ref = useRef(null);
  const barRef = useRef(null);
  const valueRef = useRef(null);
  const unique = {
    container: 'DGN-UI-LinearProgress-Determinate',
    bar: 'DGN-UI-LinearProgress-Determinate-Bar',
    value: 'DGN-UI-LinearProgress-Determinate-Value'
  };
  const keyframeStart = keyframes`
        from {
            max-height: 0;
            opacity: 0;
        }
        to {
            max-height: 20px;
            opacity: 1;
        }
    `;
  const containerCSS = css`
            animation: ${keyframeStart} 0.3s ease-in;
            background-color: ${background};
            border-radius: 4px;
            box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.1);
            height: ${cutPX(height)};
            position: relative;
            width: ${cutPX(width)};
            .${unique.bar} {
                background-color: ${theme.colors[color] || color};
                border-radius: 4px;
                bottom: 0;
                left: 0;
                position: absolute;
                top: 0;
                transition: width ${duration}s cubic-bezier(0.77, 0, 0.175, 1); /* easeInOutQuart */
            }
            .${unique.value} {
                color: ${percentColor ? percentColor : theme.colors[color] || color};
                margin: 4px 0;
                min-width: max-content;
                position: absolute;
                transform: translateX(-50%);
                transition: left ${duration}s cubic-bezier(0.77, 0, 0.175, 1); /* easeInOutQuart */
                user-select: none;
                ${valuePosition === 'bottom' ? 'top: 100%;' : 'bottom: 100%;'};
            }
        `;
  const updateValuePosition = value => {
    valueRef.current.firstChild.innerText = value + '%';
    const valHalfWidth = valueRef.current.getBoundingClientRect().width / 2;
    if (value > 90) {
      valueRef.current.style.left = `calc(${value + '%'} - ${valHalfWidth}px)`;
    } else if (value < 10) {
      valueRef.current.style.left = `calc(${value + '%'} + ${valHalfWidth}px)`;
    } else {
      valueRef.current.style.left = value + '%';
    }
  };
  const Bar = useMemo(() => jsx("div", {
    className: unique.bar,
    ref: barRef
  }), [color, duration, percent]);
  const Value = useMemo(() => showValue && jsx("span", {
    className: unique.value,
    ref: valueRef
  }, jsx(Typography, {
    type: 'p2',
    color: 'inherit',
    style: {
      textShadow: '0px 2px 4px rgba(0,0,0,0.3)'
    }
  })), [duration, percentColor, showValue, valuePosition]);
  useEffect(() => {
    const value = validatePercent(percent);
    barRef.current.style.width = value + '%';
    if (showValue) {
      updateValuePosition(value);
    }
  }, [percent, showValue]);
  return jsx("div", {
    ...props,
    className: [unique.container, className].join(' ').trim(),
    css: containerCSS,
    ref: ref
  }, Bar, Value);
});
Determinate.defaultProps = {
  background: theme.colors.brand12,
  color: theme.colors.semantic.info,
  duration: 0.1,
  height: 8,
  percent: 69,
  showValue: false,
  valuePosition: 'bottom',
  width: '100%'
};
Determinate.propTypes = {
  background: PropTypes.string,
  className: PropTypes.string,
  color: PropTypes.string,
  duration: PropTypes.number,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  percent: PropTypes.number,
  percentColor: PropTypes.string,
  showValue: PropTypes.bool,
  valuePosition: PropTypes.oneOf(['bottom', 'top']),
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};
export const DeterminateLinearProgress = /*#__PURE__*/memo(Determinate);
const LinearProgress = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(({
  background,
  className,
  color,
  determinate,
  duration,
  height,
  percent,
  percentColor,
  showValue,
  valuePosition,
  width,
  ...props
}, ref) => {
  if (!ref) ref = useRef(null);
  return determinate ? jsx(DeterminateLinearProgress, {
    background: background,
    className: className,
    color: color,
    duration: duration,
    height: height,
    percent: percent,
    percentColor: percentColor,
    showValue: showValue,
    valuePosition: valuePosition,
    width: width,
    ...props,
    ref: ref
  }) : jsx(IndeterminateLinearProgress, {
    background: background,
    className: className,
    color: color,
    height: height,
    width: width,
    ...props,
    ref: ref
  });
}));
LinearProgress.defaultProps = {
  background: theme.colors.brand12,
  color: theme.colors.semantic.info,
  determinate: false,
  duration: 0.1,
  height: 8,
  percent: 69,
  showValue: false,
  width: '100%'
};
LinearProgress.propTypes = {
  /** Color for background stroke of the linear progress. */
  background: PropTypes.string,
  /** className use in linear progress. */
  className: PropTypes.string,
  /** color of liner progress. */
  color: PropTypes.string,
  /** Determinate or Indeterminate. */
  determinate: PropTypes.bool,
  /** Time to processing. */
  duration: PropTypes.number,
  /** Dynamic height dimensions of the container. */
  height: PropTypes.number,
  /** The percent of the determinate progress. */
  percent: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Color percent-label of the progress. */
  percentColor: PropTypes.string,
  /** It's boolean to show the percent-label. */
  showValue: PropTypes.bool,
  /** Dynamic position of the progress value. */
  valuePosition: PropTypes.oneOf(['bottom', 'top']),
  /** Dynamic width dimensions of the container. */
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};
export default LinearProgress;